import { useEffect, useRef } from 'react';
import { Link, Outlet, createFileRoute, useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ApprovalProgress } from '@bounded/api-client';
import { q } from '../../api/queries';
import { useNames } from '../../api/names';
import { Countdown, ErrorNotice } from '@bounded/ui/state';
import { Empty, Skeleton } from '@bounded/ui/primitives';
import { cn } from '@bounded/ui/cn';
import { agentName, headline, isWaiting, toolName } from '../../lib/approvals';
import { useNow } from '@bounded/ui/time';
import { useFormat, useT } from '../../i18n';
import type { Messages } from '../../i18n';

type Filter = 'waiting' | 'answered';
export const Route = createFileRoute('/_app/inbox')({
  validateSearch: (s): { show: Filter } => ({ show: s.show === 'answered' ? 'answered' : 'waiting' }),
  loader: ({ context }) => context.queryClient.ensureQueryData(q.waiting()),
  component: InboxLayout,
});

function InboxLayout() {
  const t = useT(); const m = t.inbox;
  const { show } = Route.useSearch();
  // The server decides what is answerable (live device, current policy, open task); the client only drops items that expired since the last poll.
  const waitingQuery = useQuery(q.waiting());
  const historyQuery = useQuery({ ...q.history(), enabled: show === 'answered' });
  const names = useNames();
  const now = useNow(1_000);
  const navigate = useNavigate();
  const detail = useMatchRoute()({ to: '/inbox/$deviceId/$approvalId' });

  const waiting = (waitingQuery.data?.items ?? []).filter((a) => isWaiting(a, now));
  const active = show === 'waiting' ? waitingQuery : historyQuery;
  const list = show === 'waiting' ? waiting : historyQuery.data?.items ?? [];

  // j / k move through the list without leaving the keyboard. The handler reads the latest list via a ref,
  // so it isn't re-subscribed on every 1 s clock tick.
  const listRef = useRef(list);
  listRef.current = list;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || (e.target instanceof HTMLElement && e.target.closest('input,textarea,select,[contenteditable]'))) return;
      if (e.key !== 'j' && e.key !== 'k') return;
      const items = listRef.current;
      const index = detail ? items.findIndex((a) => a.device_id === detail.deviceId && a.request.approval_id === detail.approvalId) : -1;
      const next = items[Math.min(items.length - 1, Math.max(0, index + (e.key === 'j' ? 1 : -1)))];
      if (next) void navigate({ to: '/inbox/$deviceId/$approvalId', params: { deviceId: next.device_id, approvalId: next.request.approval_id }, search: { show } });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail, navigate, show]);

  return (
    <div className="-mx-4 -my-6 grid min-h-[calc(100dvh-5.5rem)] sm:-mx-8 lg:-my-8 lg:h-dvh lg:min-h-0 lg:grid-cols-[minmax(20rem,26rem)_1fr]">
      <section aria-label="Requests" className={cn('flex min-h-0 flex-col border-rule lg:border-r', detail && 'hidden lg:flex')}>
        <header className="border-b border-rule px-4 pt-6 pb-3 sm:px-6 lg:pt-8">
          <h1 className="text-2xl font-semibold tracking-[-0.01em]">{m.title}</h1>
          <p className="mt-1 text-graphite">{m.description}</p>
          <div role="tablist" className="mt-4 flex gap-1">
            {(['waiting', 'answered'] as const).map((f) => (
              <Link key={f} role="tab" aria-selected={show === f} to="/inbox" search={{ show: f }}
                className={cn('h-7 rounded-full px-3 text-sm leading-7', show === f ? 'bg-ink text-paper' : 'text-graphite hover:bg-sunken')}>
                {f === 'waiting' ? m.waitingTab(waiting.length) : m.historyTab}
              </Link>
            ))}
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {active.isPending && active.fetchStatus !== 'idle' && <div className="grid gap-3 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div>}
          {active.error && <div className="p-4"><ErrorNotice error={active.error} onRetry={() => void active.refetch()} /></div>}
          {active.data && list.length === 0 && (
            show === 'waiting'
              ? <Empty title={m.emptyTitle}>{m.emptyBody}</Empty>
              : <Empty title={m.noHistory} />
          )}
          <ul>
            {list.map((a) => <Row key={`${a.device_id}/${a.request.approval_id}`} a={a} device={names.device(a.device_id)} project={names.project(a.project_id)} show={show} now={now} />)}
          </ul>
          {show === 'answered' && historyQuery.data?.next_after && <p className="px-6 py-3 text-sm text-graphite">{m.recentOnly}</p>}
        </div>
        <p className="hidden border-t border-rule px-4 py-2 text-sm text-graphite lg:block">
          {m.keys(<kbd>j</kbd>, <kbd>k</kbd>)}
        </p>
      </section>
      <section aria-label="Request" className={cn('min-h-0 overflow-y-auto', !detail && 'hidden lg:block')}>
        <Outlet />
      </section>
    </div>
  );
}

function Row({ a, device, project, show, now }: { a: ApprovalProgress; device: string; project: string; show: Filter; now: number }) {
  const t = useT(); const f = useFormat();
  const who = agentName(a, t.agents) ?? toolName(a, t.inbox.genericTool);
  return (
    <li className="border-b border-rule">
      <Link to="/inbox/$deviceId/$approvalId" params={{ deviceId: a.device_id, approvalId: a.request.approval_id }} search={{ show }}
        className="block px-4 py-3 hover:bg-sunken data-[status=active]:bg-surface data-[status=active]:shadow-[inset_3px_0_0_var(--boundary)] sm:px-6">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate font-mono text-sm font-medium">{headline(a)}</span>
          {show === 'waiting' ? <Countdown until={a.request.expires_at} className="shrink-0 text-sm" /> : <span className="shrink-0 text-sm text-graphite">{f.ago(a.request.created_at, now)}</span>}
        </div>
        <p className="mt-1 truncate text-sm text-graphite">{t.inbox.row(who, device, project)}{show === 'answered' && `, ${historyLabel(a, t, now)}`}</p>
      </Link>
    </li>
  );
}
/** History includes pending requests the server no longer considers answerable (device revoked, policy switched, task closed). */
export function historyLabel(a: ApprovalProgress, t: Messages, now = Date.now()) {
  const s = t.inbox.states;
  if (a.status === 'decided') return a.response?.choice === 'deny' ? s.denied : s.allowed;
  if (a.status === 'pending') return Date.parse(a.request.expires_at) <= now ? s.expired : s.unanswerable;
  if (a.status === 'expired') return s.expired;
  if (a.status === 'cancelled') return s.cancelled;
  return s.other(a.status);
}
